import React from 'react';
import { graphql } from 'gatsby';
import Accordion from './Accordion';
import CompetitionForm from './CompetitionForm';
import ContainedImage from './ContainedImage';
import ContentBlocks from './ContentBlocks';
import ContentBlocksImageLinks from './ContentBlocksImageLinks';
import Content from './Content';
import ExternalVideo from './ExternalVideo';
import FeaturedProductCategories from './FeaturedProductCategories';
import FeaturedRecipes from './FeaturedRecipes';
import FullWidthImage from './FullWidthImage';
import HeadingContent from './HeadingContent';
import ImageContent from './ImageContent';
import ImageCta from './ImageCta';
import InternalVideo from './InternalVideo';
import ProductCategoryListing from './ProductCategoryListing';
import Quote from './Quote';
import StatementText from './StatementText';
import Tabs from './Tabs';
import TextBlocks from './TextBlocks';
import TextCta from './TextCta';

const ModularBlocks = ({ items, isBlogSingle }) =>
  items &&
  items.length > 0 &&
  items.map(item => (
    <React.Fragment key={item.id}>
      {item.model.apiKey === 'accordion_modular_block' && (
        <Accordion
          heading={item.instance.heading}
          items={item.instance.accordionItems}
        />
      )}
      {item.model.apiKey === 'competition_form_modular_block' && (
        <CompetitionForm
          title={item.title}
          text={item.text}
          additionalInputFields={item.additionalInputFields}
          enableAttachments={item.enableAttachments}
          attachmentLabel={item.attachmentLabel}
          submittedText={item.submittedText}
        />
      )}
      {item.model.apiKey === 'contained_image_modular_block' && (
        <ContainedImage image={item.image} isBlogSingle={isBlogSingle} />
      )}
      {item.model.apiKey === 'content_blocks_modular_block' && (
        <ContentBlocks heading={item.heading} items={item.contentBlocks} />
      )}
      {item.model.apiKey === 'content_blocks_image_links_modular_block' && (
        <ContentBlocksImageLinks
          heading={item.heading}
          contentBlocks={item.contentBlocks}
          imageLinks={item.imageLinks}
        />
      )}
      {item.model.apiKey === 'content_modular_block' && (
        <Content
          html={item.content}
          contain={item.contain}
          twoColumns={item.twoColumns}
          highlight={item.highlight}
          isBlogSingle={isBlogSingle}
        />
      )}
      {item.model.apiKey === 'external_video_modular_block' && (
        <ExternalVideo video={item.externalVideo} isBlogSingle={isBlogSingle} />
      )}
      {item.model.apiKey === 'featured_product_categories_modular_block' && (
        <FeaturedProductCategories
          heading={item.heading}
          items={item.featuredProductCategories}
        />
      )}
      {item.model.apiKey === 'featured_recipes_modular_block' && (
        <FeaturedRecipes heading={item.heading} items={item.featuredRecipes} />
      )}
      {item.model.apiKey === 'full_width_image_modular_block' && (
        <FullWidthImage image={item.image} isBlogSingle={isBlogSingle} />
      )}
      {item.model.apiKey === 'heading_content_modular_block' && (
        <HeadingContent heading={item.heading} content={item.content} />
      )}
      {item.model.apiKey === 'image_content_modular_block_v1' && (
        <ImageContent
          image={item.image}
          heading={item.heading}
          text={item.text}
          link={item.link}
          flip={item.flip}
          version={1}
        />
      )}
      {item.model.apiKey === 'image_content_modular_block_v2' && (
        <ImageContent
          image={item.image}
          heading={item.heading}
          text={item.text}
          link={item.link}
          flip={item.flip}
          version={2}
        />
      )}
      {item.model.apiKey === 'image_cta_modular_block' && (
        <ImageCta
          heading={item.heading}
          text={item.text}
          link={item.link}
          image={item.image}
        />
      )}
      {item.model.apiKey === 'internal_video_modular_block' && (
        <InternalVideo video={item.internalVideo} />
      )}
      {item.model.apiKey === 'product_categories_modular_block' && (
        <ProductCategoryListing
          heading={item.heading}
          items={item.productCategories}
        />
      )}
      {item.model.apiKey === 'quote_modular_block' && (
        <Quote text={item.text} image={item.image} />
      )}
      {item.model.apiKey === 'statement_text_modular_block' && (
        <StatementText text={item.text} />
      )}
      {item.model.apiKey === 'tabs_modular_block' && (
        <Tabs heading={item.instance.heading} items={item.instance.tabs} />
      )}
      {item.model.apiKey === 'text_blocks_modular_block' && (
        <TextBlocks heading={item.heading} items={item.textBlocks} />
      )}
      {item.model.apiKey === 'text_cta_modular_block' && (
        <TextCta heading={item.heading} link={item.link} />
      )}
    </React.Fragment>
  ));

export default ModularBlocks;

export const ModularBlockFragments = graphql`
  fragment AccordionModularBlockFragment on DatoCmsAccordionModularBlock {
    id
    model {
      apiKey
    }
    instance {
      heading
      accordionItems {
        id
        heading
        content
      }
    }
  }

  fragment CompetitionFormModularBlockFragment on DatoCmsCompetitionFormModularBlock {
    id
    model {
      apiKey
    }
    title
    text
    additionalInputFields {
      name
      required
    }
    enableAttachments
    attachmentLabel
    submittedText
  }

  fragment ContainedImageModularBlockFragment on DatoCmsContainedImageModularBlock {
    id
    model {
      apiKey
    }
    image {
      gatsbyImageData(width: 1180)
      alt
    }
  }

  fragment ContentBlocksModularBlockFragment on DatoCmsContentBlocksModularBlock {
    id
    model {
      apiKey
    }
    heading
    contentBlocks {
      text
    }
  }

  fragment ContentBlocksImageLinksModularBlockFragment on DatoCmsContentBlocksImageLinksModularBlock {
    id
    model {
      apiKey
    }
    heading
    contentBlocks {
      text
    }
    imageLinks {
      image {
        gatsbyImageData(width: 360, height: 420)
        alt
      }
      icon {
        url
        alt
      }
      text
      link {
        ... on DatoCmsLink {
          page {
            ...LinkFragment
          }
        }
        ... on DatoCmsExternalLink {
          url
        }
      }
    }
  }

  fragment ContentModularBlockFragment on DatoCmsContentModularBlock {
    id
    model {
      apiKey
    }
    content
    twoColumns
    highlight
    contain
  }

  fragment ExternalVideoModularBlockFragment on DatoCmsExternalVideoModularBlock {
    id
    model {
      apiKey
    }
    externalVideo {
      height
      width
      provider
      providerUid
      url
    }
  }

  fragment FeaturedProductCategoriesModularBlockFragment on DatoCmsFeaturedProductCategoriesModularBlock {
    id
    model {
      apiKey
    }
    heading
    featuredProductCategories {
      ...ProductCategoryCardFragment
    }
  }

  fragment FeaturedRecipesModularBlockFragment on DatoCmsFeaturedRecipesModularBlock {
    id
    model {
      apiKey
    }
    heading
    featuredRecipes {
      ...RecipeCardFragment
    }
  }

  fragment FullWidthImageModularBlockFragment on DatoCmsFullWidthImageModularBlock {
    id
    model {
      apiKey
    }
    image {
      gatsbyImageData(width: 2000, layout: FULL_WIDTH)
      alt
    }
  }

  fragment HeadingContentModularBlockFragment on DatoCmsHeadingContentModularBlock {
    id
    model {
      apiKey
    }
    heading
    content
  }

  fragment ImageContentModularBlockV1Fragment on DatoCmsImageContentModularBlockV1 {
    id
    model {
      apiKey
    }
    image {
      gatsbyImageData(width: 710, height: 750)
      alt
    }
    heading
    text
    link {
      text
      page {
        ...LinkFragment
      }
    }
    flip
  }
  fragment ImageContentModularBlockV2Fragment on DatoCmsImageContentModularBlockV2 {
    id
    model {
      apiKey
    }
    image {
      gatsbyImageData(width: 850, height: 700)
      alt
    }
    heading
    text
    link {
      text
      page {
        ...LinkFragment
      }
    }
    flip
  }

  fragment ImageCtaModularBlockFragment on DatoCmsImageCtaModularBlock {
    id
    model {
      apiKey
    }
    heading
    text
    link {
      text
      page {
        ...LinkFragment
      }
    }
    image {
      gatsbyImageData(width: 1000, height: 480)
      alt
    }
  }

  fragment InternalVideoModularBlockFragment on DatoCmsInternalVideoModularBlock {
    id
    model {
      apiKey
    }
    internalVideo {
      format
      url
      video {
        thumbnailUrl
        mp4Url
      }
    }
  }

  fragment ProductCategoriesModularBlockFragment on DatoCmsProductCategoriesModularBlock {
    id
    model {
      apiKey
    }
    heading
    productCategories {
      ...ProductCategoryCardFragment
    }
  }

  fragment QuoteModularBlockFragment on DatoCmsQuoteModularBlock {
    id
    model {
      apiKey
    }
    text
    image {
      gatsbyImageData(width: 560, height: 600)
      alt
    }
  }

  fragment StatementTextModularBlockFragment on DatoCmsStatementTextModularBlock {
    id
    model {
      apiKey
    }
    text
  }

  fragment TabsModularBlockFragment on DatoCmsTabsModularBlock {
    id
    model {
      apiKey
    }
    instance {
      heading
      tabs {
        id
        heading
        content
      }
    }
  }

  fragment TextBlocksModularBlockFragment on DatoCmsTextBlocksModularBlock {
    id
    model {
      apiKey
    }
    heading
    textBlocks {
      id
      heading
      text
    }
  }

  fragment TextCtaModularBlockFragment on DatoCmsTextCtaModularBlock {
    id
    model {
      apiKey
    }
    heading
    link {
      text
      page {
        ...LinkFragment
      }
    }
  }
`;
